/**
 * Hardens the app against casual browser DevTools inspection in production.
 *
 * NOTE: There is no 100% reliable way to block DevTools in a browser.
 * These measures raise the bar significantly but cannot stop a determined user
 * who opens DevTools before loading the page or uses browser flags/extensions.
 * Real security must always be enforced server-side.
 *
 * Call `disableBrowserDevTools()` once at startup.
 * Call `enableDevToolsForSuperAdmin()` after confirming the user is a super admin
 * to lift all restrictions for that session.
 */

let _blocked = false;
let _intervalId: ReturnType<typeof setInterval> | null = null;

const _contextMenuHandler = (e: MouseEvent): void => {
  if (_blocked) e.preventDefault();
};

const _keydownHandler = (e: KeyboardEvent): void => {
  if (!_blocked) return;

  const ctrl = e.ctrlKey || e.metaKey;
  const shift = e.shiftKey;
  const key = e.key.toUpperCase();

  const isDevToolsShortcut =
    e.key === 'F12' ||
    (ctrl && shift && key === 'I') ||
    (ctrl && shift && key === 'J') ||
    (ctrl && shift && key === 'C') ||
    (ctrl && shift && key === 'K') ||
    (ctrl && key === 'U');

  if (isDevToolsShortcut) {
    e.preventDefault();
    e.stopPropagation();
  }
};

export function disableBrowserDevTools(): void {
  _blocked = true;

  // 1. Disable right-click context menu (blocks "Inspect Element")
  document.addEventListener('contextmenu', _contextMenuHandler);

  // 2. Block common DevTools keyboard shortcuts (capture phase)
  document.addEventListener('keydown', _keydownHandler, true);

  // 3. Debugger timing trap
  // When DevTools is open the `debugger` statement pauses execution.
  // The elapsed time will be far above the normal threshold, revealing the open state.
  const DEVTOOLS_OPEN_THRESHOLD_MS = 100;

  _intervalId = setInterval(() => {
    if (!_blocked) return;

    const start = performance.now();
    // eslint-disable-next-line no-debugger
    debugger;
    const elapsed = performance.now() - start;

    if (elapsed > DEVTOOLS_OPEN_THRESHOLD_MS) {
      document.documentElement.innerHTML = '';
      window.location.replace(window.location.origin);
    }
  }, 1500);
}

/** Call this once the store confirms the current user is a super admin. */
export function enableDevToolsForSuperAdmin(): void {
  _blocked = false;

  if (_intervalId !== null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }

  document.removeEventListener('contextmenu', _contextMenuHandler);
  document.removeEventListener('keydown', _keydownHandler, true);
}
